import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { toast, Toaster } from "sonner";
import {
  ScanLine, CheckCheck, Zap, Package, Loader2, X,
  DollarSign, PlusCircle, Volume2,
} from "lucide-react";
import { lookupByGtin, adoptMasterWithPrices, createProduct, listProducts } from "@/lib/api";
import useHardwareScanner from "@/hooks/useHardwareScanner";
import { beepSuccess, beepError, beepConfirm } from "@/lib/beep";

/**
 * "Modo Precios Express" — pantalla dedicada para poner precios a los ~4,000
 * productos del catálogo maestro con una pistola HID Bluetooth. Sin taps.
 * Flujo: escaneo → overlay con datos del producto → foco en precio → Enter
 * cierra + guarda + flash verde + queda lista para el siguiente scan.
 */
export default function PreciosExpress() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [overlay, setOverlay] = useState(null);
  // overlay: { status: 'in_store'|'in_catalog'|'unknown', gtin, master?, product?, ... }
  const [salePrice, setSalePrice] = useState("");
  const [stock, setStock] = useState("");
  const [flash, setFlash] = useState(null); // 'ok' | 'ko'
  const [prodCount, setProdCount] = useState(0);
  const priceRef = useRef(null);

  const refreshCount = () =>
    listProducts().then((rows) => setProdCount((rows || []).length)).catch(() => {});
  useEffect(() => { refreshCount(); }, []);

  const flashOnce = (kind) => {
    setFlash(kind);
    setTimeout(() => setFlash(null), 450);
  };

  const closeOverlay = () => {
    setOverlay(null); setSalePrice(""); setStock("");
  };

  const handleScan = async (gtin) => {
    if (busy || overlay) return; // ignore while overlay open — user must submit price first
    setBusy(true);
    try {
      const r = await lookupByGtin(gtin);
      if (r.status === "in_store") {
        beepConfirm();
        toast(`✓ Ya lo tienes — ${r.product.name}: $${r.product.sale_price}`);
        setBusy(false);
        return;
      }
      if (r.status === "in_catalog" || r.status === "in_external") {
        beepSuccess();
        setOverlay(r);
        setTimeout(() => priceRef.current?.focus(), 60);
      } else {
        beepError();
        setOverlay({ status: "unknown", gtin: r.gtin || gtin });
        setTimeout(() => priceRef.current?.focus(), 60);
      }
    } catch {
      beepError();
      toast.error("Error en la búsqueda");
    } finally { setBusy(false); }
  };

  useHardwareScanner({
    enabled: !overlay && !busy,
    onScan: handleScan,
    minLen: 8, maxLen: 14, gapMs: 50,
  });

  const submitPrice = async () => {
    const p = parseFloat(salePrice);
    if (!p || p <= 0) { toast.error("Precio requerido"); beepError(); return; }
    setBusy(true);
    try {
      if (overlay.status === "in_catalog" && overlay.master) {
        await adoptMasterWithPrices([{
          master_product_id: overlay.master.id,
          sale_price: p,
          stock_quantity: parseFloat(stock) || 0,
        }]);
      } else if (overlay.status === "in_external" && overlay.external) {
        await createProduct({
          name: [overlay.external.brand, overlay.external.product_name].filter(Boolean).join(" "),
          category: overlay.external.category || "Otros",
          sale_price: p,
          cost_price: 0,
          stock_quantity: parseFloat(stock) || 0,
          min_stock_alert: 0,
          unit: "pieza",
          barcode: overlay.gtin,
        });
      } else {
        // unknown: needs name too
        toast.error("Ese código no está en el catálogo — usa el modal para captura manual");
        setBusy(false);
        return;
      }
      flashOnce("ok");
      beepConfirm();
      closeOverlay();
      refreshCount();
    } catch (e) {
      flashOnce("ko");
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  // Unknown quick-register form uses controlled name
  const [unkName, setUnkName] = useState("");
  const submitUnknown = async () => {
    const n = unkName.trim();
    const p = parseFloat(salePrice);
    if (!n || !p) { toast.error("Nombre y precio requeridos"); beepError(); return; }
    setBusy(true);
    try {
      await createProduct({
        name: n, category: "Otros", sale_price: p, cost_price: 0,
        stock_quantity: parseFloat(stock) || 0, min_stock_alert: 0,
        unit: "pieza", barcode: overlay.gtin,
      });
      flashOnce("ok"); beepConfirm();
      setUnkName("");
      closeOverlay(); refreshCount();
    } catch (e) {
      flashOnce("ko");
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const goal = 4000;
  const pct = Math.min(100, Math.round((prodCount / goal) * 100));

  return (
    <div className="min-h-screen bg-[#050505] text-white relative">
      <DashNav />
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`fixed inset-0 z-30 pointer-events-none ${flash === "ok" ? "bg-[#CCFF00]" : "bg-[#FF5A00]"}`}
          />
        )}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-8 relative z-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
              / Precios express con pistola HID
            </p>
            <h1 className="font-display text-4xl md:text-5xl leading-none tracking-tighter">
              Modo <span className="text-[#CCFF00]">precios</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#CCFF00]/40 bg-[#CCFF00]/[0.08] px-3 py-1.5" data-testid="scanner-ready">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#CCFF00] opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#CCFF00]" />
              </span>
              <span className="font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">Scanner Ready</span>
            </div>
            <div className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5" data-testid="pricing-progress">
              <span className="font-mono text-[10px] tracking-widest text-white/60 uppercase">
                Priced: <span className="text-white">{prodCount}</span> / {goal.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-8">
          <motion.div
            initial={{ width: 0 }} animate={{ width: `${pct}%` }}
            className="h-full bg-gradient-to-r from-[#CCFF00] to-[#00D4FF]"
          />
        </div>

        {/* Idle instructions */}
        {!overlay && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 md:p-14 text-center" data-testid="pricing-idle">
            <ScanLine size={40} className="mx-auto mb-4 text-[#CCFF00]" />
            <h2 className="font-satoshi text-2xl md:text-3xl mb-2">Prende tu pistola HID</h2>
            <p className="text-white/60 max-w-lg mx-auto mb-4">
              No necesitas tocar nada. Solo escanea el código de barras de cada producto
              y aparecerá la ventana para poner el precio. Enter guarda y regresa al scanner listo.
            </p>
            <div className="flex items-center justify-center gap-2 text-white/40 text-xs font-mono uppercase tracking-widest">
              <Volume2 size={12} className="text-[#CCFF00]" />
              beeps activos · esperando código
            </div>
          </div>
        )}

        {/* Overlay */}
        <AnimatePresence>
          {overlay && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
              data-testid="pricing-overlay"
            >
              <motion.div
                initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
                className="w-full max-w-xl bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8"
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">GTIN {overlay.gtin}</p>
                  <button onClick={closeOverlay} className="text-white/40 hover:text-white" data-testid="pricing-close">
                    <X size={18} />
                  </button>
                </div>

                {overlay.status === "in_catalog" && overlay.master && (
                  <div data-testid="pricing-in-catalog">
                    <div className="flex gap-4 items-start mb-6">
                      <div className="w-24 h-24 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {overlay.master.image_url ? (
                          <img src={overlay.master.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package size={32} className="text-white/30" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-2xl md:text-3xl tracking-tighter leading-none">
                          {overlay.master.brand} {overlay.master.global_product_name || overlay.master.product_name}
                        </h3>
                        <p className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-2">
                          {overlay.master.subcategory || overlay.master.category || "—"}
                          {overlay.master.net_content_ml ? ` · ${overlay.master.net_content_ml} ${overlay.master.unit || "ml"}` : ""}
                        </p>
                      </div>
                    </div>
                    <PriceInput priceRef={priceRef} salePrice={salePrice} setSalePrice={setSalePrice}
                      stock={stock} setStock={setStock} onSubmit={submitPrice} busy={busy} />
                  </div>
                )}

                {overlay.status === "in_external" && overlay.external && (
                  <div data-testid="pricing-in-external">
                    <div className="flex gap-4 items-start mb-6">
                      <div className="w-24 h-24 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                        {overlay.external.image_url ? (
                          <img src={overlay.external.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package size={32} className="text-white/30" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[9px] text-[#00D4FF] uppercase tracking-widest mb-1">Vía Open Food Facts</p>
                        <h3 className="font-display text-2xl tracking-tighter leading-none">
                          {overlay.external.brand} {overlay.external.product_name}
                        </h3>
                        <p className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-2">
                          {overlay.external.quantity || ""} {overlay.external.category ? `· ${overlay.external.category}` : ""}
                        </p>
                      </div>
                    </div>
                    <PriceInput priceRef={priceRef} salePrice={salePrice} setSalePrice={setSalePrice}
                      stock={stock} setStock={setStock} onSubmit={submitPrice} busy={busy} />
                  </div>
                )}

                {overlay.status === "unknown" && (
                  <div data-testid="pricing-unknown">
                    <div className="rounded-2xl border border-[#FF5A00]/30 bg-[#FF5A00]/[0.06] p-4 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <PlusCircle size={14} className="text-[#FF5A00]" />
                        <span className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase">Código no está en el catálogo</span>
                      </div>
                      <p className="text-white/60 text-xs">¿Registrar como producto temporal ligado a este GTIN?</p>
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre del producto</label>
                      <input value={unkName} onChange={(e) => setUnkName(e.target.value)}
                        placeholder="Escríbelo — ej. Pan Casero 500g"
                        className="w-full mt-1 mb-3 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#FF5A00] focus:outline-none"
                        data-testid="pricing-unknown-name" />
                    </div>
                    <PriceInput priceRef={priceRef} salePrice={salePrice} setSalePrice={setSalePrice}
                      stock={stock} setStock={setStock} onSubmit={submitUnknown} busy={busy} label="Guardar producto temporal" />
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

function PriceInput({ priceRef, salePrice, setSalePrice, stock, setStock, onSubmit, busy, label = "Guardar y siguiente" }) {
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); onSubmit(); } };
  return (
    <div>
      <div className="grid grid-cols-[1fr_100px] gap-3 mb-4">
        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Precio de venta *</label>
          <div className="mt-1 flex items-center bg-black/50 border border-white/10 rounded-xl px-4 py-4 focus-within:border-[#CCFF00]">
            <span className="text-[#CCFF00] mr-2 text-xl font-satoshi">$</span>
            <input
              ref={priceRef}
              value={salePrice}
              onChange={(e) => setSalePrice(e.target.value.replace(/[^0-9.]/g, ""))}
              onKeyDown={onKey}
              placeholder="0.00"
              inputMode="decimal"
              className="w-full bg-transparent text-white text-2xl font-satoshi focus:outline-none placeholder-white/25"
              data-testid="pricing-price-input"
            />
          </div>
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Stock</label>
          <input value={stock} onChange={(e) => setStock(e.target.value.replace(/[^0-9.]/g, ""))}
            onKeyDown={onKey} placeholder="0" inputMode="decimal"
            className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-3 py-4 text-white text-lg focus:border-[#CCFF00] focus:outline-none placeholder-white/25"
            data-testid="pricing-stock-input" />
        </div>
      </div>
      <button
        onClick={onSubmit}
        disabled={busy || !salePrice}
        className="w-full py-4 rounded-full bg-[#CCFF00] text-black font-mono text-sm uppercase tracking-widest hover:bg-white transition disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="pricing-save"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />}
        {label} <span className="text-black/60 text-[10px]">· Enter</span>
      </button>
      <p className="text-center text-white/40 text-[11px] mt-3">Al guardar, el scanner queda listo para el próximo código.</p>
    </div>
  );
}
